import { Button, Checkbox, Group, NumberInput, Select, Stack, Text, Textarea } from '@mantine/core';
import { parse, print } from '@marketing-tool/shared';
import React from 'react';

export type AdControlLogicEditorProps = {
    days: number;
    logic: string;
    allowAutoStart: boolean;
    minActiveAdCount: number;
    onSave: (values: { days: number; logic: string; allowAutoStart: boolean; minActiveAdCount: number }) => void;
    onCancel: () => void;
    onDelete: () => void;
};

export const AdControlLogicEditor = ({
    days,
    logic,
    allowAutoStart,
    minActiveAdCount,
    onSave,
    onCancel,
    onDelete,
}: AdControlLogicEditorProps) => {
    const [currentDays, setCurrentDays] = React.useState(days);
    const [currentLogic, setCurrentLogic] = React.useState(logic);
    const [currentAllowAutoStart, setAllowAutoStart] = React.useState(allowAutoStart);
    const [currentMinActiveAdCount, setMinActiveAdCount] = React.useState(minActiveAdCount);
    const [error, setError] = React.useState<string | null>(null);

    const handleSave = () => {
        try {
            const formatted = print(parse(currentLogic));
            setError(null);
            onSave({
                days: currentDays,
                logic: formatted,
                allowAutoStart: currentAllowAutoStart,
                minActiveAdCount: currentMinActiveAdCount,
            });
            setCurrentLogic(formatted);
        } catch (err: any) {
            if (err.pos) {
                const { rowBegin, rowEnd, columnBegin, columnEnd } = err.pos;
                console.log(err.pos);
                const lines = currentLogic.split('\n');
                let selection: string;

                if (rowBegin === rowEnd) {
                    selection = lines[rowBegin - 1]!.slice(columnBegin - 1, columnEnd);
                } else {
                    selection = lines[rowBegin - 1]!.slice(columnBegin - 1);
                    for (let row = rowBegin; row < rowEnd - 2; row += 1) {
                        selection += '\n' + lines[row];
                    }
                    selection += '\n' + lines[rowEnd - 1]!.slice(0, columnEnd);
                }

                setError(`Error parsing at row ${err.pos.rowBegin}, col ${err.pos.columnBegin}: ${selection}`);
            } else {
                setError(err.message);
            }
        }
    };

    const handleDelete = () => {
        onDelete();
    };

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setCurrentLogic(event.target.value);
    };

    const handleCancel = () => {
        onCancel();
        setCurrentLogic(logic);
    };

    return (
        <Stack gap={0}>
            <Group gap="md" mb="xs" align="center">
                <Text c="dimmed">
                    <strong>Using data starting</strong>
                </Text>
                <Select
                    value={currentDays.toString()}
                    onChange={(value) => {
                        if (value) setCurrentDays(parseInt(value));
                    }}
                    data={[
                        { value: '1', label: 'Yesterday (1 day ago)' },
                        { value: '2', label: '2 days ago' },
                        { value: '3', label: '3 days ago' },
                        { value: '4', label: '4 days ago' },
                        { value: '5', label: '5 days ago' },
                        { value: '6', label: '6 days ago' },
                        { value: '7', label: '1 week ago (7 days ago)' },
                    ]}
                />
                <Text c="dimmed">
                    <strong>morning (00:00 AM)</strong>
                </Text>
            </Group>
            <Textarea
                value={currentLogic}
                onChange={handleChange}
                minRows={16}
                autosize
                variant="filled"
                styles={{
                    input: {
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                    },
                }}
            />
            <Checkbox
                checked={currentAllowAutoStart}
                onChange={(event) => setAllowAutoStart(event.currentTarget.checked)}
                label="Allow auto-starting"
                mt="xs"
            />
            <Group gap="xs" mt="xs" align="center">
                <Text>Minimum number of ads per ad group to keep auto-started:</Text>
                <NumberInput
                    value={currentMinActiveAdCount}
                    onChange={(val) => setMinActiveAdCount(typeof val === 'number' ? val : 0)}
                    w={100}
                />
            </Group>
            {error && (
                <Text component="pre" c="red" mt="xs" style={{ whiteSpace: 'pre-wrap' }}>
                    {error}
                </Text>
            )}
            <Group justify="space-between" mt="xs">
                <Button color="red" onClick={handleDelete}>
                    Delete
                </Button>
                <Group gap="md">
                    <Button onClick={handleSave}>Save</Button>
                    <Button variant="subtle" onClick={handleCancel}>
                        Cancel
                    </Button>
                </Group>
            </Group>
        </Stack>
    );
};
